import { IconLogout } from "@tabler/icons-react";
import React from "react";
import { toast } from "sonner";

import { Loader } from "@/components/Loader";
import { useTeam } from "@/components/providers/team";
import { useFetchUserInvites } from "@/components/queries/user";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";

export function DeclineTeamDialog({ inviteId }: { inviteId: string }) {
	const [isLoading, setIsLoading] = React.useState(false);
	const { refetchInvites } = useFetchUserInvites();
	const { refresh } = useTeam();
	const isMountedRef = React.useRef(true);
	React.useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	return (
		<AlertDialog>
			<AlertDialogTrigger
				className={buttonVariants({
					variant: "destructive",
					size: "icon-sm",
				})}
				aria-label="Decline team invite"
			>
				<IconLogout className="h-4 w-4" />
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Decline Team</AlertDialogTitle>
					<AlertDialogDescription>
						Are you sure you want to decline this team? This action cannot be
						undone.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel>Cancel</AlertDialogCancel>
					<AlertDialogAction
						onClick={async () => {
							setIsLoading(true);
							try {
								await requestWithHandling<void>(() =>
									apiClient.api.userTeamsInviteDelete({ id: inviteId }),
								);
								toast.success("Team declined successfully");
								await refresh();
								await refetchInvites();
							} catch (error) {
								toast.error("Failed to decline team", {
									description:
										error instanceof TypedClientApiError
											? error.message
											: "An unknown error occurred.",
								});
							} finally {
								if (isMountedRef.current) {
									setIsLoading(false);
								}
							}
						}}
						disabled={isLoading}
						className="bg-destructive hover:bg-destructive/90"
					>
						{isLoading ? <Loader /> : "Decline"}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
